import { Component, OnInit } from '@angular/core';
import { EmployeesService } from '../../../employees/employees.service';
import { FarmersService } from '../../farmers.service';
import { Employee } from '../../../employees/employees.model';
import { Farmer, FarmerVisit } from '../../farmers.model';
import { finalize } from 'rxjs';
import { PaginatedResponse } from '../../../common/models/common.model';
import { buildFilterQuery, convertUTCtoIST } from '../../../utils';

enum ViewState {
  EMPLOYEES = 'employees',
  FARMERS = 'farmers',
  VISITS = 'visits',
}

/** Each view pages independently, so each keeps its own cursor and totals. */
interface PageState {
  page: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const emptyPage = (): PageState => ({ page: 1, total: 0, hasNext: false, hasPrev: false });

@Component({
  selector: 'app-employee-farmers',
  standalone: false,
  templateUrl: './employee-farmers.component.html',
  styleUrl: './employee-farmers.component.scss',
})
export class EmployeeFarmersComponent implements OnInit {
  viewState = ViewState;
  currentView: ViewState = ViewState.EMPLOYEES;

  employees: Employee[] = [];
  farmers: Farmer[] = [];
  visits: FarmerVisit[] = [];

  selectedEmployee: Employee | null = null;
  selectedFarmer: Farmer | null = null;

  isLoading = false;
  isExportingFarmers = false;
  searchTerm: string = '';

  readonly pageSize = 10;
  employeesPage: PageState = emptyPage();
  farmersPage: PageState = emptyPage();
  visitsPage: PageState = emptyPage();

  private searchDebounceTimer!: any;

  constructor(
    private employeesService: EmployeesService,
    private farmersService: FarmersService
  ) {}

  ngOnInit(): void {
    this.getEmployees();
  }

  getEmployees(): void {
    this.isLoading = true;

    const reqParams = buildFilterQuery({
      page: this.employeesPage.page,
      search: this.searchTerm,
      team: 'sales',
    });

    this.employeesService
      .list(reqParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((res: PaginatedResponse<Employee>) => {
        this.employees = res.results || [];
        this._applyPage(this.employeesPage, res);
      });
  }

  onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.employeesPage.page = 1;
      this.getEmployees();
    }, 500);
  }

  onEmployeesPageChange(page: number): void {
    this.employeesPage.page = page;
    this.getEmployees();
  }

  onFarmersPageChange(page: number): void {
    this.farmersPage.page = page;
    this.getFarmers();
  }

  onVisitsPageChange(page: number): void {
    this.visitsPage.page = page;
    this.getVisits();
  }

  selectEmployee(employee: Employee): void {
    this.selectedEmployee = employee;
    this.farmersPage = emptyPage();
    this.getFarmers();
  }

  getFarmers(): void {
    if (!this.selectedEmployee) return;

    this.isLoading = true;
    this.farmersService
      .get_farmers({ employee_id: this.selectedEmployee.user.id, page: this.farmersPage.page })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(res => {
        this.farmers = res.results || [];
        this._applyPage(this.farmersPage, res);
        this.currentView = ViewState.FARMERS;
      });
  }

  selectFarmer(farmer: Farmer): void {
    this.selectedFarmer = farmer;
    this.visitsPage = emptyPage();
    this.getVisits();
  }

  getVisits(): void {
    if (!this.selectedFarmer) return;

    this.isLoading = true;
    this.farmersService
      .get_farmer_visits({ farmer_id: this.selectedFarmer.id, page: this.visitsPage.page })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(res => {
        this.visits = res.results || [];
        this._applyPage(this.visitsPage, res);
        this.currentView = ViewState.VISITS;
      });
  }

  /** The number a visit carries in the full history, not just on this page. */
  visitNumber(index: number): number {
    return (this.visitsPage.page - 1) * this.pageSize + index + 1;
  }

  backToEmployees(): void {
    this.currentView = ViewState.EMPLOYEES;
    this.selectedEmployee = null;
    this.selectedFarmer = null;
    this.farmers = [];
    this.visits = [];
    this.farmersPage = emptyPage();
    this.visitsPage = emptyPage();
  }

  onExportEmployeeFarmers(): void {
    if (!this.selectedEmployee) return;

    this.isExportingFarmers = true;
    const employeeId = this.selectedEmployee.user.id;
    const name = this.selectedEmployee.user.full_name?.replace(/\s+/g, '_') || employeeId;

    this.farmersService
      .export(String(employeeId))
      .pipe(finalize(() => (this.isExportingFarmers = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `farmers_${name}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  backToFarmers(): void {
    this.currentView = ViewState.FARMERS;
    this.selectedFarmer = null;
    this.visits = [];
    this.visitsPage = emptyPage();
  }

  private _applyPage(state: PageState, res: PaginatedResponse<unknown>): void {
    state.total = res.count;
    state.hasNext = !!res.next;
    state.hasPrev = !!res.previous;
  }

  protected readonly convertUTCtoIST = convertUTCtoIST;
}
